import { useState } from 'react';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import { openApiDocument } from './spec';
import { customCss } from './scalarTheme';
import { SessionPanel } from './SessionPanel';

/**
 * Partner API documentation — renders the Game Platform OpenAPI 3.1 spec with
 * Scalar (cURL / Ruby / Node samples + a live "Test Request" client).
 *
 * SessionPanel supplies the X-Platform-Session token the Player API needs
 * (quick preview, or the real signed POST /sessions flow) and we pre-fill it
 * into Scalar's auth. The Try-It server follows the backend this console is
 * connected to, so the token is always valid for the environment it hits.
 */
export function ApiPage() {
  const [token, setToken] = useState<string | null>(null);

  const apiBase =
    (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ||
    window.location.origin;

  // No global preferredSecurityScheme — each endpoint shows ITS OWN auth
  // (Create session / server-to-server = API key + signature; Player API =
  // session token). Pre-fill the player token only where PlatformSession is
  // used, so live "Try it" works on the Player API section. The key is omitted
  // entirely when there's no token (exactOptionalPropertyTypes forbids undefined).
  const playerAuth = token
    ? { authentication: { securitySchemes: { PlatformSession: { value: token } } } }
    : {};

  return (
    <div className="flex h-full flex-col">
      <SessionPanel apiBase={apiBase} token={token} onToken={setToken} />

      <div className="min-h-0 flex-1">
        <ApiReferenceReact
          key={token ?? 'no-token'}
          configuration={{
            content: openApiDocument as Record<string, unknown>,
            servers: [{ url: apiBase, description: 'Backend this console is connected to' }],
            theme: 'default',
            layout: 'modern',
            darkMode: true,
            hideDownloadButton: false,
            customCss,
            metaData: {
              title: 'Repackz Game Platform API',
              description: 'Build your own player UI on top of Repackz card packs',
            },
            ...playerAuth,
          }}
        />
      </div>
    </div>
  );
}
